/**
 * Sites Configuration Manager
 * Loads sites from static JSON file and syncs with localStorage
 */

const SITES_STORAGE_KEY = 'my-dashboard-sites';
const SITES_CONFIG_URL = '/sites-config.json';
const SITES_VERSION_KEY = 'my-dashboard-sites-version';

/**
 * Load sites from static config file
 * Used once at application startup
 */
export async function loadSitesFromConfig() {
  try {
    const response = await fetch(SITES_CONFIG_URL);
    if (!response.ok) {
      console.warn('Could not load sites-config.json');
      return null;
    }
    const data = await response.json();
    return data.sites || [];
  } catch (e) {
    console.warn('Error loading sites config:', e);
    return null;
  }
}

/**
 * Initialize sites from config file
 * Called once on app startup
 * Only loads from file if no sites exist in localStorage
 */
export async function initializeSites() {
  try {
    // Check if sites already exist
    const existing = localStorage.getItem(SITES_STORAGE_KEY);
    if (existing) {
      // Sites already loaded, skip
      return JSON.parse(existing);
    }

    // If API is configured, try loading sites from the remote API first
    try {
      const api = process.env.REACT_APP_API_URL;
      const token = process.env.REACT_APP_API_TOKEN;
      if (api) {
        const url = `${api.replace(/\/$/, '')}/api/sites`;
        const res = await fetch(url, { headers: { ...(token ? { 'x-api-token': token } : {}) } });
        if (res.ok) {
          const data = await res.json();
          const fromApi = data.sites || [];
          if (fromApi.length > 0) {
            saveSites(fromApi);
            localStorage.setItem(SITES_VERSION_KEY, '1.0');
            console.log(`Loaded ${fromApi.length} sites from API (${url})`);
            return fromApi;
          }
        } else {
          console.warn('API returned', res.status, await res.text().catch(()=>''));
        }
      }
    } catch (e) {
      console.warn('Failed to fetch sites from API:', e);
    }

    // Load from config file
    const configSites = await loadSitesFromConfig();
    if (configSites && configSites.length > 0) {
      saveSites(configSites);
      localStorage.setItem(SITES_VERSION_KEY, '1.0');
      console.log(`Loaded ${configSites.length} sites from config file`);
      return configSites;
    }

    // Fallback: empty array
    return [];
  } catch (e) {
    console.error('Error initializing sites:', e);
    return [];
  }
}

/**
 * Get all sites from localStorage
 */
export function getSites() {
  try {
    const raw = localStorage.getItem(SITES_STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch (e) {
    console.warn('Error parsing sites:', e);
    return [];
  }
}

/**
 * Save sites to localStorage
 * Automatically called when admin adds/edits/removes sites
 */
export function saveSites(sites) {
  try {
    localStorage.setItem(SITES_STORAGE_KEY, JSON.stringify(sites));
    // Update last modified timestamp
    localStorage.setItem('my-dashboard-sites-lastModified', new Date().toISOString());
  } catch (e) {
    console.warn('Error saving sites:', e);
  }
}

/**
 * Get site by ID
 */
export function getSiteById(id) {
  const sites = getSites();
  return sites.find(s => s.id === id);
}

/**
 * Add new site
 */
export function addSite(siteData) {
  const sites = getSites();
  const newSite = {
    id: `site-${Date.now()}`,
    ...siteData,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  const updated = [...sites, newSite];
  saveSites(updated);
  // Try to sync to remote API if configured (and notify on failure)
  try {
    const api = process.env.REACT_APP_API_URL;
    const token = process.env.REACT_APP_API_TOKEN;
    if (api) {
      fetch(`${api.replace(/\/$/, '')}/api/sites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'x-api-token': token } : {}) },
        body: JSON.stringify(newSite)
      }).then(async res => {
        if (!res.ok) {
          const txt = await res.text().catch(()=>res.statusText||'');
          const err = txt || `HTTP ${res.status}`;
          // lazy import notify to avoid circular deps
          import('./notify').then(m => m.notify('error', `Échec sync site: ${err}`)).catch(()=>{});
        }
      }).catch(err => {
        import('./notify').then(m => m.notify('error', `Impossible de joindre l'API: ${err.message}`)).catch(()=>{});
      });
    }
  } catch (e) {
    import('./notify').then(m => m.notify('error', `Erreur de sync: ${e.message}`)).catch(()=>{});
  }
  return newSite;
}

/**
 * Update site
 */
export function updateSite(id, updates) {
  const sites = getSites();
  const index = sites.findIndex(s => s.id === id);
  if (index === -1) return null;

  sites[index] = {
    ...sites[index],
    ...updates,
    updatedAt: new Date().toISOString()
  };
  saveSites(sites);
  // Try to sync update to remote API if configured (and notify on failure)
  try {
    const api = process.env.REACT_APP_API_URL;
    const token = process.env.REACT_APP_API_TOKEN;
    if (api) {
      fetch(`${api.replace(/\/$/, '')}/api/sites/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'x-api-token': token } : {}) },
        body: JSON.stringify(sites[index])
      }).then(async res => {
        if (!res.ok) {
          const txt = await res.text().catch(()=>res.statusText||'');
          import('./notify').then(m => m.notify('error', `Échec sync update: ${txt || res.status}`)).catch(()=>{});
        }
      }).catch(err => {
        import('./notify').then(m => m.notify('error', `Impossible de joindre l'API: ${err.message}`)).catch(()=>{});
      });
    }
  } catch (e) {
    import('./notify').then(m => m.notify('error', `Erreur de sync: ${e.message}`)).catch(()=>{});
  }
  return sites[index];
}

/**
 * Delete site
 */
export function deleteSite(id) {
  const sites = getSites();
  const filtered = sites.filter(s => s.id !== id);
  saveSites(filtered);
  // Try to sync delete to remote API if configured (and notify on failure)
  try {
    const api = process.env.REACT_APP_API_URL;
    const token = process.env.REACT_APP_API_TOKEN;
    if (api) {
      fetch(`${api.replace(/\/$/, '')}/api/sites/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: { ...(token ? { 'x-api-token': token } : {}) }
      }).then(async res => {
        if (!res.ok) {
          const txt = await res.text().catch(()=>res.statusText||'');
          import('./notify').then(m => m.notify('error', `Échec sync delete: ${txt || res.status}`)).catch(()=>{});
        }
      }).catch(err => {
        import('./notify').then(m => m.notify('error', `Impossible de joindre l'API: ${err.message}`)).catch(()=>{});
      });
    }
  } catch (e) {
    import('./notify').then(m => m.notify('error', `Erreur de sync: ${e.message}`)).catch(()=>{});
  }
  return true;
}

/**
 * Get sites by category
 */
export function getSitesByCategory(category) {
  const sites = getSites();
  return sites.filter(s => (s.category || 'General') === category);
}

/**
 * Get all categories
 */
export function getCategories() {
  const sites = getSites();
  const categories = new Set(sites.map(s => s.category || 'General'));
  return Array.from(categories);
}

/**
 * Check if sites were loaded from config (first time)
 */
export function isFirstLoad() {
  return !localStorage.getItem(SITES_VERSION_KEY);
}

/**
 * Export sites as JSON
 */
export function exportSites() {
  const sites = getSites();
  return {
    sites,
    exportedAt: new Date().toISOString(),
    version: '1.0'
  };
}

/**
 * Import sites from JSON (merge with existing)
 */
export function importSites(data, merge = true) {
  try {
    if (!data.sites || !Array.isArray(data.sites)) {
      throw new Error('Invalid import data');
    }

    if (merge) {
      const existing = getSites();
      const merged = [...existing];
      
      data.sites.forEach(importedSite => {
        const existingIndex = merged.findIndex(s => s.id === importedSite.id);
        if (existingIndex === -1) {
          merged.push(importedSite);
        } else {
          merged[existingIndex] = { ...merged[existingIndex], ...importedSite };
        }
      });
      
      saveSites(merged);
      return merged;
    } else {
      saveSites(data.sites);
      return data.sites;
    }
  } catch (e) {
    console.error('Error importing sites:', e);
    return null;
  }
}

const sitesAPI = {
  loadSitesFromConfig,
  initializeSites,
  getSites,
  saveSites,
  getSiteById,
  addSite,
  updateSite,
  deleteSite,
  getSitesByCategory,
  getCategories,
  isFirstLoad,
  exportSites,
  importSites
};

export default sitesAPI;
